import { THEME_PREFERENCES, THEME_PREFERENCE_VALUES } from '@/styles/constants';
import type {
  ThemePreferenceType,
  ThemePreferenceValue,
  ThemeSetterCallback,
} from '@/styles/types';
import { useEffect, useRef } from 'react';

const getSystemMediaQueryList = () => {
  const watcher = window.matchMedia('(prefers-color-scheme: dark)');
  return watcher;
};

const getSystemThemeValueToken = (
  event: MediaQueryListEvent | MediaQueryList
) => {
  return event.matches
    ? THEME_PREFERENCE_VALUES.DARK
    : THEME_PREFERENCE_VALUES.LIGHT;
};

export const useSystemThemeListener = (
  themePreference: ThemePreferenceType,
  themePreferenceValue: ThemePreferenceValue | undefined,
  setThemeValue: ThemeSetterCallback
) => {
  const preferenceRef = useRef(themePreference);
  const isSystemThemePreference = themePreference === THEME_PREFERENCES.SYSTEM;

  useEffect(() => {
    const watcher = getSystemMediaQueryList();
    if (!watcher) return;

    const onThemeChange = (event: MediaQueryListEvent) => {
      if (isSystemThemePreference) {
        const themeToken = getSystemThemeValueToken(event);
        setThemeValue(themeToken);
      }
    };

    // Initial sync on mount
    if (isSystemThemePreference) {
      const themeToken = getSystemThemeValueToken(watcher);
      setThemeValue(themeToken);
    }

    watcher.addEventListener('change', onThemeChange);
    return () => watcher.removeEventListener('change', onThemeChange);
  }, [isSystemThemePreference, setThemeValue]);
  /**
   * when theme preference changes from system to light or dark and back to system, we need to detect the current
   * system theme and set it since the watcher is attached on mount and the initial theme will not be set
   * in between toggles
   */
  useEffect(() => {
    const preferenceChanged = preferenceRef.current !== themePreference;
    if (preferenceChanged) {
      preferenceRef.current = themePreference;
      if (isSystemThemePreference) {
        const systemTheme = getSystemThemeValueToken(getSystemMediaQueryList());
        const themeValueChanged = themePreferenceValue !== systemTheme;
        if (themeValueChanged) setThemeValue(systemTheme);
      }
    }
  }, [
    themePreference,
    themePreferenceValue,
    setThemeValue,
    isSystemThemePreference,
  ]);
};
